import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import '../shared.css';
import '../KG/Modal.css';

interface CreateInstanceModalProps {
  kgId: string;
  versionId: string;
  onClose: () => void;
  onCreated: () => void;
}

const CreateInstanceModal: React.FC<CreateInstanceModalProps> = ({
  kgId,
  versionId,
  onClose,
  onCreated,
}) => {
  const [instanceTypes, setInstanceTypes] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInstanceTypes();
  }, []);

  const loadInstanceTypes = async () => {
    try {
      setLoadingTypes(true);
      const response: any = await apiService.getInstanceTypes();
      if (response.data && Array.isArray(response.data)) {
        setInstanceTypes(response.data);
        if (response.data.length > 0) {
          setSelectedType(response.data[0]);
        }
      }
    } catch (err: any) {
      console.error('Error loading instance types:', err);
      // Fallback to common types if API fails
      setInstanceTypes(['db.t3.micro', 'db.t3.small', 'db.t3.medium']);
      setSelectedType('db.t3.micro');
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedType) {
      setError('Please select an instance type');
      return;
    }

    try {
      setLoading(true);
      await apiService.createInstance(kgId, versionId, selectedType);
      onCreated();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to create instance'));
      console.error('Error creating instance:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Launch New Instance</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {loadingTypes ? (
              <div className="page-loader"><div className="page-spinner" /></div>
            ) : (
              <div className="form-group">
                <label>Instance Type *</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="form-control"
                >
                  {instanceTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                  Choose the instance size based on your knowledge graph requirements.
                </small>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="tab-btn tab-btn--ghost"
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="tab-btn tab-btn--primary" disabled={loading || loadingTypes}>
              {loading ? 'Launching...' : 'Launch Instance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInstanceModal;
